/**
 * Video / media embed resolver — v79.118
 *
 * Predicts (without hitting the network) whether a URL can be
 * embedded inside the Adukoo app, and returns either an iframe src
 * OR a "this site blocks embedding, open externally" verdict.
 *
 * Design rules:
 *  - Positive predictions ONLY. If a provider is known to block iframe
 *    embedding via X-Frame-Options / ToS, we skip the embed attempt
 *    entirely and surface a clear "Open in <app>" button instead. No
 *    half-loaded placeholders, no timeout dances.
 *  - Provider list is curated; the "unknown" bucket falls through to
 *    a soft-attempt path only for links that clearly LOOK like video
 *    content (extensions .mp4/.webm/.mov). Random http URLs never try.
 */

// ─── Known providers we CAN embed ────────────────────────────────────
// Every entry returns { type, src, aspect: '16/9' | '9/16' | ...,
// title, needsAllowFullscreen: true }
function _youtube(url) {
    // Handles youtube.com/watch?v=, youtu.be/, youtube.com/shorts/,
    // youtube.com/embed/, youtube-nocookie.com, m.youtube.com
    const m = url.match(/(?:youtube\.com|youtube-nocookie\.com)\/(?:watch\?v=|embed\/|shorts\/|v\/)([\w-]{11})|youtu\.be\/([\w-]{11})/i);
    if (!m) return null;
    const id = m[1] || m[2];
    return {
        type: "youtube",
        provider: "YouTube",
        src: `https://www.youtube-nocookie.com/embed/${id}?autoplay=1&rel=0&modestbranding=1`,
        aspect: url.includes("/shorts/") ? "9/16" : "16/9",
        allowFullscreen: true,
        embed: "iframe",
        allow: "accelerometer; autoplay; clipboard-write; encrypted-media; fullscreen; gyroscope; picture-in-picture; web-share",
    };
}
function _vimeo(url) {
    // vimeo.com/<id> or player.vimeo.com/video/<id>
    const m = url.match(/vimeo\.com\/(?:video\/)?(\d{5,12})/);
    if (!m) return null;
    return {
        type: "vimeo",
        provider: "Vimeo",
        src: `https://player.vimeo.com/video/${m[1]}?autoplay=1&title=0&byline=0`,
        aspect: "16/9",
        allowFullscreen: true,
        embed: "iframe",
        allow: "autoplay; fullscreen; picture-in-picture",
    };
}
function _dailymotion(url) {
    const m = url.match(/dailymotion\.com\/(?:embed\/)?video\/([a-zA-Z0-9]+)/) || url.match(/dai\.ly\/([a-zA-Z0-9]+)/);
    if (!m) return null;
    return {
        type: "dailymotion",
        provider: "Dailymotion",
        src: `https://www.dailymotion.com/embed/video/${m[1]}?autoplay=1`,
        aspect: "16/9",
        allowFullscreen: true,
        embed: "iframe",
        allow: "autoplay; fullscreen; picture-in-picture",
    };
}
function _rumble(url) {
    // Rumble share URLs like https://rumble.com/v3xxx-title.html — needs
    // the /embed/vXXXX/ variant which we can't derive without a lookup.
    // So we only embed if the URL already contains /embed/.
    if (!/rumble\.com\/embed\//i.test(url)) return null;
    return {
        type: "rumble",
        provider: "Rumble",
        src: url + (url.includes("?") ? "&" : "?") + "autoplay=1",
        aspect: "16/9",
        allowFullscreen: true,
        embed: "iframe",
        allow: "autoplay; fullscreen",
    };
}
function _twitch(url) {
    // Live channels: twitch.tv/<name>  →  player.twitch.tv/?channel=name
    // VOD: twitch.tv/videos/<id>       →  player.twitch.tv/?video=v<id>
    const parent = typeof window !== "undefined" ? window.location.hostname : "adukoo.com";
    const vod = url.match(/twitch\.tv\/videos\/(\d+)/);
    if (vod) {
        return {
            type: "twitch", provider: "Twitch",
            src: `https://player.twitch.tv/?video=v${vod[1]}&parent=${parent}&autoplay=true`,
            aspect: "16/9", allowFullscreen: true, embed: "iframe",
            allow: "autoplay; fullscreen",
        };
    }
    const ch = url.match(/twitch\.tv\/(?!videos)([a-zA-Z0-9_]{3,25})/);
    if (ch) {
        return {
            type: "twitch", provider: "Twitch",
            src: `https://player.twitch.tv/?channel=${ch[1]}&parent=${parent}&autoplay=true`,
            aspect: "16/9", allowFullscreen: true, embed: "iframe",
            allow: "autoplay; fullscreen",
        };
    }
    return null;
}
function _soundcloud(url) {
    if (!/soundcloud\.com\//i.test(url)) return null;
    return {
        type: "soundcloud", provider: "SoundCloud",
        src: `https://w.soundcloud.com/player/?url=${encodeURIComponent(url)}&auto_play=true&hide_related=true`,
        aspect: "auto",
        height: 166,
        allowFullscreen: false,
        embed: "iframe",
        allow: "autoplay",
    };
}
function _spotify(url) {
    // https://open.spotify.com/track/<id>  →  /embed/track/<id>
    const m = url.match(/open\.spotify\.com\/(track|episode|album|playlist|show)\/([a-zA-Z0-9]+)/);
    if (!m) return null;
    return {
        type: "spotify", provider: "Spotify",
        src: `https://open.spotify.com/embed/${m[1]}/${m[2]}?utm_source=generator`,
        aspect: "auto",
        height: m[1] === "track" || m[1] === "episode" ? 152 : 380,
        allowFullscreen: false,
        embed: "iframe",
        allow: "autoplay; clipboard-write; encrypted-media; picture-in-picture",
    };
}
function _directVideo(url) {
    // .mp4 / .webm / .mov / .ogv — HTML5 <video> tag
    if (!/\.(mp4|webm|mov|ogv|ogg|m4v)(\?|$)/i.test(url)) return null;
    return {
        type: "direct-video", provider: "Video",
        src: url,
        aspect: "16/9", allowFullscreen: true,
        embed: "video",
    };
}
function _directAudio(url) {
    if (!/\.(mp3|wav|aac|flac|m4a)(\?|$)/i.test(url)) return null;
    return {
        type: "direct-audio", provider: "Audio", src: url,
        aspect: "auto", height: 60, allowFullscreen: false,
        embed: "audio",
    };
}

// ─── Known providers we CANNOT embed ──────────────────────────────────
// Any URL matching these gets the "open externally" treatment BEFORE we
// try to load anything.
const UNEMBEDDABLE_HOSTS = [
    { rx: /(?:^|\.)instagram\.com/i,   label: "Instagram",  reason: "Instagram blocks in-app playback." },
    { rx: /(?:^|\.)facebook\.com/i,    label: "Facebook",   reason: "Facebook blocks in-app playback." },
    { rx: /(?:^|\.)fb\.watch/i,        label: "Facebook",   reason: "Facebook blocks in-app playback." },
    { rx: /(?:^|\.)tiktok\.com/i,      label: "TikTok",     reason: "TikTok requires opening in its own app." },
    { rx: /(?:^|\.)twitter\.com/i,     label: "X (Twitter)", reason: "X (Twitter) blocks iframe embedding." },
    { rx: /(?:^|\.)x\.com/i,           label: "X (Twitter)", reason: "X (Twitter) blocks iframe embedding." },
    { rx: /(?:^|\.)threads\.net/i,     label: "Threads",    reason: "Threads blocks iframe embedding." },
    { rx: /(?:^|\.)snapchat\.com/i,    label: "Snapchat",   reason: "Snapchat requires opening in its own app." },
    { rx: /(?:^|\.)reddit\.com/i,      label: "Reddit",     reason: "Reddit video blocks embedding." },
    { rx: /(?:^|\.)pinterest\.com/i,   label: "Pinterest",  reason: "Pinterest blocks iframe embedding." },
    { rx: /(?:^|\.)linkedin\.com/i,    label: "LinkedIn",   reason: "LinkedIn blocks iframe embedding." },
];

/**
 * Main resolver.
 * @param {string} url
 * @returns {null | { embed: 'iframe'|'video'|'audio', ... } | { blocked: true, provider, reason, openHref }}
 */
export function resolveEmbed(url) {
    if (!url || typeof url !== "string") return null;
    const clean = url.trim();

    // 1. Known-blocked provider fast fail
    for (const b of UNEMBEDDABLE_HOSTS) {
        if (b.rx.test(clean)) {
            return { blocked: true, provider: b.label, reason: b.reason, openHref: clean };
        }
    }

    // 2. Known-good providers
    return (
        _youtube(clean) ||
        _vimeo(clean) ||
        _dailymotion(clean) ||
        _rumble(clean) ||
        _twitch(clean) ||
        _soundcloud(clean) ||
        _spotify(clean) ||
        _directVideo(clean) ||
        _directAudio(clean) ||
        null
    );
}

/**
 * True if the URL is one we know we CAN embed.
 */
export function canEmbed(url) {
    const r = resolveEmbed(url);
    return !!(r && !r.blocked);
}
